import { useEffect } from "react"
import { getMetaContent } from "~/common/get-meta-content"
import { isAndroid, isIOS } from "~/common/platform"
import appStoreBadge from "~/images/store-badges/app-store-badge"
import playStoreBadge from "~/images/store-badges/play-store-badge"
import { brandAppName } from "~/common/brand"
import { getBrandWordmark } from "~/common/brand-assets"
import { AppText } from "~/ui/app-text"
import { useThemeColor } from "~/hooks/use-theme-color"
import { SCREENS, EVENTS, EVENT_IDS, trackEvent } from "~/common/analytics"
import { useScreenTracking } from "~/hooks/use-screen-tracking"
import { DownloadQr } from "~/share/components/download-qr"

const APP_STORE_URL = getMetaContent<string>("APP_STORE_URL")
const PLAY_STORE_URL = getMetaContent<string>("PLAY_STORE_URL")

const getRedirectTo = () => {
  if (isIOS()) {
    return APP_STORE_URL
  } else if (isAndroid()) {
    return PLAY_STORE_URL
  }
  return null
}

export const DownloadScreen = () => {
  useThemeColor("#2D92FF")
  const redirectTo = getRedirectTo()
  const brandLogo = getBrandWordmark({
    ramble: "h-auto w-[300px] max-w-full",
    chatbcc: "h-[104px] w-[257px]",
  })

  useScreenTracking(SCREENS.Download, {
    redirect_to: redirectTo || undefined,
  })

  useEffect(() => {
    if (redirectTo) {
      window.location.href = redirectTo
    }
  }, [redirectTo])

  const handleStoreButtonClick = (storeType: "app_store" | "play_store") => {
    trackEvent(EVENTS.ITEM_PRESSED, {
      unique_id:
        storeType === "app_store"
          ? EVENT_IDS.DOWNLOAD_APP_STORE_CLICKED
          : EVENT_IDS.DOWNLOAD_PLAY_STORE_CLICKED,
    })
  }

  return (
    <div className="relative flex min-h-screen bg-[#2D92FF]" data-testid="download-screen">
      <div className="mx-auto my-auto max-w-7xl px-6 py-8">
        <div className="mx-auto max-w-[440px] space-y-8 text-center">
          {/* Logo section */}
          <div className="flex justify-center">
            <img
              src={brandLogo.src}
              alt={`${brandAppName()} Logo`}
              width={brandLogo.width}
              height={brandLogo.height}
              className={brandLogo.className}
              loading="eager"
            />
          </div>

          {/* Title and description */}
          <div className="md:space-y-4">
            {/* Download QR (Desktop) */}
            <div className="max-md:hidden">
              <DownloadQr className="pb-0 pt-0" hideCaption />
            </div>

            <AppText variant="body1-medium" className="text-[#141010]">
              Download the app to join the community!
            </AppText>
          </div>

          <div className="space-y-4">
            {/* Five Star Rating */}
            <div className="flex justify-center space-x-1">
              {[...Array(5)].map((_, i) => (
                <svg
                  key={i}
                  width="36"
                  height="36"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 1.5L15.09 7.76L22 8.77L17 13.64L18.18 20.52L12 17.27L5.82 20.52L7 13.64L2 8.77L8.91 7.76L12 1.5Z"
                    fill="#FFCE31"
                  />
                </svg>
              ))}
            </div>

            {/* Store badges */}
            <div className="flex flex-row items-center justify-center gap-3">
              <a
                href={APP_STORE_URL}
                className="transition-transform hover:scale-105 active:scale-95"
                aria-label="Download on App Store"
                onClick={() => handleStoreButtonClick("app_store")}
              >
                <img
                  src={appStoreBadge.src}
                  alt="Download on App Store"
                  height={60}
                  className="h-[60px] object-contain xs:pl-[23px]"
                />
              </a>
              <a
                href={PLAY_STORE_URL}
                className="transition-transform hover:scale-105 active:scale-95"
                aria-label="Get it on Google Play"
                onClick={() => handleStoreButtonClick("play_store")}
              >
                <img
                  src={playStoreBadge.src}
                  alt="Get it on Google Play"
                  height={60}
                  className="h-[60px] object-contain"
                />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
